import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, X } from 'lucide-react';
import { ReferencePickerField } from '@/components/forms/ReferencePickerField';
import { FormFieldRenderer } from '@/components/forms/FormFieldRenderer';
import { FileMultiField } from '@/components/autoforms/FileMultiField';
import { detectFieldType, buildTypesFromTypeString } from '@/lib/formTypes';

// ============================================================================
// LayoutTabularSection — рендер ТЧ в макете кастомной формы (вынесено из
// CustomLayoutRenderer case 'tabularSection', ~210 строк — снижение цикломатики).
// Виды: 'field' (множественное значение чипами / коллекция файлов) и таблица
// (колонки по типу: ссылка/файл/дата/число/булево/текст).
// ============================================================================

interface LayoutTabularSectionProps {
  el: any;
  idx: number;
  tsName: string;
  rows: any[];
  allColsMeta: any[];
  tsMeta?: any;
  disabled?: boolean;
  enabled?: boolean;
  readOnly?: boolean;
  setTsData: (fn: (prev: any) => any) => void;
  onPickMulti?: (tsName: string, colMeta: any) => void;
  onPickTs?: (tsName: string, rowIndex: number, colAttr: any) => void;
}

/** Обновление одной ячейки строки ТЧ. */
function cellSetter(tsName: string, setTsData: LayoutTabularSectionProps['setTsData']) {
  return (ri: number, colName: string, v: unknown) =>
    setTsData((prev: any) => ({
      ...prev,
      [tsName]: (prev[tsName] || []).map((r: any, i: number) => (i === ri ? { ...r, [colName]: v } : r)),
    }));
}

/** Удаление строки ТЧ. */
function rowRemover(tsName: string, setTsData: LayoutTabularSectionProps['setTsData']) {
  return (ri: number) =>
    setTsData((prev: any) => ({ ...prev, [tsName]: (prev[tsName] || []).filter((_: any, i: number) => i !== ri) }));
}

export function LayoutTabularSection({ el, idx, tsName, rows, allColsMeta, tsMeta, disabled, enabled, readOnly, setTsData, onPickMulti, onPickTs }: LayoutTabularSectionProps) {
  const layoutCols = el.columns || [];
  const cols: any[] = layoutCols;
  const tsReadonly = readOnly || el.viewOnly;
  const setCell = cellSetter(tsName, setTsData);
  const removeRow = rowRemover(tsName, setTsData);
  const addRow = () => setTsData((prev: any) => ({ ...prev, [tsName]: [...(prev[tsName] || []), {}] }));

  // ВИД «Поле» (множественное значение чипами / коллекция файлов)
  if (el.viewMode === 'field' && el.multiselectField) {
    const colName = el.multiselectField;
    const meta = allColsMeta.find((m: any) => m.name === colName);
    const tsAttr = tsMeta?.attributes?.find((a: any) => a.name === colName);
    const refTable = meta?.refTable || tsAttr?.refTable || '';
    const colType = detectFieldType(meta?.type || tsAttr?.type || 'reference', refTable);
    const colMeta = { name: colName, type: meta?.type || tsAttr?.type || 'reference', refTable, types: tsAttr?.types || meta?.types || [], label: (cols.find((c) => c.attribute === colName) as any)?.label || meta?.description || colName };
    const picked = rows.filter((r: any) => r[colName]);
    const isFileCol = colType === 'file' || refTable === 'files' || refTable === 'files_po_dokumentam';
    if (isFileCol) {
      return (
        <FileMultiField
          tsName={tsName}
          colName={colName}
          rows={rows}
          view={((el as any).widget || 'tile') as any}
          settings={(el as any).widgetSettings as any}
          onAdd={() => onPickMulti?.(tsName, colMeta)}
          onRemove={(id) => setTsData((prev: any) => ({ ...prev, [tsName]: (prev[tsName] || []).filter((r: any) => String(r[colName] ?? '') !== id) }))}
          disabled={disabled || tsReadonly}
          title={el.title || ''}
        />
      );
    }
    return (
      <div key={`ts-${idx}`} className={`space-y-1 border rounded-lg p-3 ${disabled ? 'opacity-60' : ''}`}>
        <div className="flex items-center gap-2">
          {el.showLabel === true && <Label className="text-xs font-medium">{el.title || ''}</Label>}
          {!tsReadonly && enabled && (
            <Button size="sm" className="h-6 px-2 text-xs" onClick={() => onPickMulti?.(tsName, colMeta)}>
              <Plus className="h-3 w-3 mr-1" />Добавить
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {picked.length === 0 && <span className="text-[10px] text-muted-foreground">Нет значений</span>}
          {picked.map((row: any, ri: number) => (
            <span key={ri} className="flex items-center gap-1.5 border rounded px-2 py-1 text-xs bg-muted/30">
              <span className="min-w-0 truncate">{String(row[colName + '_presentation'] ?? row[colName] ?? '')}</span>
              {!tsReadonly && enabled && (
                <button className="text-muted-foreground hover:text-destructive" title="Удалить" onClick={() => removeRow(ri)}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // ТАБЛИЦА
  return (
    <div key={`ts-${idx}`} className={`flex flex-col flex-1 min-h-0 space-y-1 border rounded-lg p-3 ${disabled ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-2 shrink-0">
        {!tsReadonly && enabled && (
          <Button size="sm" className="h-6 px-2 text-xs" onClick={addRow}>
            <Plus className="h-3 w-3 mr-1" />Добавить
          </Button>
        )}
        {el.showLabel === true && <Label className="text-xs font-medium">{el.title || ''}</Label>}
      </div>
      {rows.length > 0 ? (
        <div className="flex-1 overflow-auto min-h-0 border rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                {cols.map((c: any) => {
                  const meta = allColsMeta.find((m: any) => m.name === c.attribute);
                  return <th key={c.attribute} className="px-3 py-1.5 text-xs font-medium text-left whitespace-nowrap">{c.label || meta?.description || c.attribute}</th>;
                })}
                {!tsReadonly && enabled && <th className="w-8" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, ri: number) => (
                <tr key={ri} className="border-b last:border-b-0">
                  {cols.map((c: any, ci: number) => {
                    const colName = c.attribute;
                    const meta = allColsMeta.find((m: any) => m.name === colName);
                    const tsAttr = tsMeta?.attributes?.find((a: any) => a.name === colName);
                    const refTable = c.refTable || meta?.refTable || tsAttr?.refTable || meta?.fkTable || '';
                    const colType = detectFieldType(meta?.type || c.type || tsAttr?.type || '', meta?.fkTable || refTable);
                    const isRefByTable = (colType === 'reference' || colType === 'select') && !!refTable;
                    let rawValue = row.attributes?.[colName] ?? row[colName];
                    if (rawValue === undefined && colName) {
                      const rowKeys = Object.keys(row);
                      const ciKey = rowKeys.find((k) => k.toLowerCase() === colName.toLowerCase());
                      if (ciKey) rawValue = row[ciKey];
                      if (rawValue === undefined) {
                        const strippedKey = rowKeys.find((k) => k.replace(/_Представление$/i, '').toLowerCase() === colName.toLowerCase());
                        if (strippedKey) rawValue = row[strippedKey];
                      }
                    }
                    const cellDisabled = !enabled || tsReadonly || c.readonly || c.readOnly;

                    if (isRefByTable) {
                      return (
                        <td key={`${ri}-${colName}-${ci}`} className="px-2 py-1 min-w-[140px]">
                          <ReferencePickerField
                            value={rawValue ?? ''}
                            refTable={refTable}
                            displayValue={String(row[colName + '_presentation'] ?? '')}
                            selectionParams={tsAttr?.selectionParams}
                            selectionLinks={tsAttr?.selectionLinks}
                            contextValues={row}
                            disabled={cellDisabled}
                            compact
                            onChange={(v) => setCell(ri, colName, v)}
                          />
                        </td>
                      );
                    }
                    if (colType === 'enum') {
                      // Перечисление: селект синонимов значений (SSoT — сервер).
                      // ref_table = КОД перечисления (resolveUIType вернёт 'enum').
                      return (
                        <td key={`${ri}-${colName}-${ci}`} className="px-2 py-1 min-w-[140px]">
                          <FormFieldRenderer
                            field={{ name: colName, label: '', data_type: 'enumeration', ref_table: refTable, placeholder: '', options: tsAttr?.options || meta?.options }}
                            value={String(rawValue ?? '')}
                            disabled={cellDisabled}
                            onChange={(v) => setCell(ri, colName, v)}
                            compact
                          />
                        </td>
                      );
                    }
                    if (colType === 'reference' || colType === 'select') {
                      let resolvedTypes: any[] = [];
                      if (c.types && c.types.length > 0) {
                        resolvedTypes = c.types;
                      } else if (tsMeta) {
                        const tsAttr2 = tsMeta.attributes.find((a: any) => a.name === colName);
                        if (tsAttr2?.types?.length > 0) resolvedTypes = tsAttr2.types;
                      }
                      if (resolvedTypes.length === 0) resolvedTypes = buildTypesFromTypeString(meta?.type || c.type || '');
                      const colAttr = { name: colName, type: meta?.type || c.type, description: c.label || meta?.description, types: resolvedTypes, comment: meta?.comment };
                      const refType = colType === 'reference' ? ((resolvedTypes.length > 0 ? (resolvedTypes[0].fullName || '') : (meta?.type || c.type || '')).replace('Ссылка.', '.')) : '';
                      return (
                        <td key={`${ri}-${colName}-${ci}`} className="px-2 py-1">
                          <FormFieldRenderer
                            field={{ name: colName, label: '', data_type: colType === 'reference' ? 'reference' : (meta?.type || c.type || 'text'), ref_table: refTable, placeholder: '', options: tsAttr?.options || meta?.options }}
                            value={String(rawValue ?? '')}
                            displayValue={String(row[colName + '_presentation'] ?? '')}
                            disabled={cellDisabled}
                            onChange={(v) => setCell(ri, colName, v)}
                            onPick={!cellDisabled && colType === 'reference' && resolvedTypes.length > 0 ? () => onPickTs?.(tsName, ri, colAttr) : undefined}
                            compact
                          />
                        </td>
                      );
                    }
                    if (colType === 'file') {
                      const fileColAttr = { name: colName, type: 'file', description: c.label || meta?.description, types: [], refTable: 'files' };
                      return (
                        <td key={`${ri}-${colName}-${ci}`} className="px-2 py-1 min-w-[180px]">
                          <FormFieldRenderer
                            field={{ name: colName, label: '', data_type: 'file', ref_table: '', placeholder: '' }}
                            value={String(rawValue ?? '')}
                            displayValue={String(row[colName + '_presentation'] ?? '')}
                            disabled={cellDisabled}
                            onChange={(v) => setCell(ri, colName, v)}
                            onPick={!cellDisabled ? () => onPickTs?.(tsName, ri, fileColAttr) : undefined}
                            compact
                          />
                        </td>
                      );
                    }
                    if (colType === 'date') {
                      let dv = String(rawValue ?? '');
                      if (/^\d{2}\.\d{2}\.\d{4}/.test(dv)) {
                        const [d, m, y] = dv.split('.');
                        dv = `${y}-${m}-${d}`;
                      }
                      if (/^\d{4}-\d{2}-\d{2}$/.test(dv)) dv = dv + 'T00:00';
                      if (dv.includes('T') && dv.length > 16) dv = dv.substring(0, 16);
                      return (
                        <td key={`${ri}-${colName}-${ci}`} className="px-2 py-1">
                          <input type="datetime-local" value={dv} onChange={(e) => setCell(ri, colName, e.target.value)} disabled={cellDisabled} className="h-7 text-xs w-full border rounded px-2" />
                        </td>
                      );
                    }
                    if (colType === 'number') {
                      const numVal = Number(rawValue);
                      const formatted = (!isNaN(numVal) && numVal !== 0)
                        ? new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numVal)
                        : String(rawValue ?? '');
                      return (
                        <td key={`${ri}-${colName}-${ci}`} className="px-2 py-1">
                          <input type="text" value={formatted} onChange={(e) => { const parsed = Number(e.target.value.replace(/\s/g, '').replace(',', '.')); setCell(ri, colName, isNaN(parsed) ? '' : parsed); }} disabled={cellDisabled} className="h-7 text-xs w-full border rounded px-2 text-right font-mono" />
                        </td>
                      );
                    }
                    if (colType === 'boolean') {
                      return (
                        <td key={`${ri}-${colName}-${ci}`} className="px-2 py-1">
                          <input type="checkbox" checked={Boolean(rawValue)} onChange={(e) => setCell(ri, colName, e.target.checked)} disabled={cellDisabled} className="h-4 w-4" />
                        </td>
                      );
                    }
                    return (
                      <td key={`${ri}-${colName}-${ci}`} className="px-2 py-1">
                        <Input value={String(rawValue ?? '')} onChange={(e) => setCell(ri, colName, e.target.value)} disabled={cellDisabled} className="h-7 text-xs" />
                      </td>
                    );
                  })}
                  {!tsReadonly && enabled && (
                    <td className="px-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeRow(ri)}>
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-[10px] text-muted-foreground">Нет данных</div>
      )}
    </div>
  );
}
